import { useMemo, useRef, useState } from 'react';

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
  Portal,
  useDisclosure,
} from '@chakra-ui/react';
import { Plus } from 'lucide-react';

import { IconButton } from '@/components/core';
import { useRecentPrompts } from '@/store/prompts';

import {
  hasPromptTemplates,
  needsFill,
  previewOf,
  PromptChipInput,
  PromptCommandMenu,
  PromptFill,
  PromptMenuSection,
  PromptTemplateChip,
  SuggestionItemGroup,
  templateChip,
  usePromptFillContext,
} from '../Editor';

type CommandPickerProps = {
  suggestions: SuggestionItemGroup[];
  /** The agent whose prompts these are; its Recent row needs it (#596). */
  agentId?: number;
  disabled?: boolean;
  /** A template (switch on) or today's string (switch off). */
  onSelect: (item: PromptChipInput) => void;
};

/**
 * The `+` picker: every group's prompts on Command Menu, with its own search.
 * A template with `{variables}` opens its fill step in the same card first.
 */
export function CommandPicker({
  suggestions,
  agentId,
  disabled = false,
  onSelect,
}: CommandPickerProps) {
  const { isOpen, onOpen, onClose: closePopover } = useDisclosure();
  const triggerRef = useRef<HTMLButtonElement>(null);
  // A template with a Context Variable always opens its fill step (#632).
  const fillContext = usePromptFillContext();
  const [filling, setFilling] = useState<{
    key: string;
    chip: PromptTemplateChip;
  } | null>(null);

  const onClose = () => {
    setFilling(null);
    closePopover();
  };

  const templatesOn = hasPromptTemplates(suggestions);
  // Recent (switch on, #596): the caller's last templates for this agent,
  // first; the menu's own search filters it like the groups.
  const { data: recentTemplates } = useRecentPrompts(
    agentId,
    templatesOn && isOpen
  );

  const { sections, chipsByKey } = useMemo(() => {
    const chips = new Map<string, PromptChipInput>();
    const recent = templatesOn ? (recentTemplates ?? []) : [];
    const recentSections: PromptMenuSection[] = recent.length
      ? [
          {
            key: 'recent',
            label: 'Recent',
            rows: recent.map(template => {
              const key = `recent-${template.id}`;
              chips.set(key, templateChip(template));
              return {
                key,
                label: template.title,
                kind: 'entry' as const,
                recent: true,
                mine: !!template.mine,
                preview: previewOf(templateChip(template)),
              };
            }),
          },
        ]
      : [];

    const nextSections: PromptMenuSection[] = suggestions.map(
      (group, groupIndex) => {
        const groupKey = `p${group.id ?? groupIndex}-${groupIndex}`;
        const keywords = `${group.label} ${group.highlight}`;
        const entries = templatesOn
          ? (group.templates ?? []).map(template => ({
              chip: templateChip(template) as PromptChipInput,
              title: template.title,
              mine: !!template.mine,
            }))
          : group.items.map(item => ({
              chip: item as PromptChipInput,
              title: item,
              mine: false,
            }));

        return {
          key: groupKey,
          label: group.label,
          rows: entries.map((entry, index) => {
            const key = `${groupKey}-${index}`;
            chips.set(key, entry.chip);
            return {
              key,
              label: entry.title,
              iconUrl: group.icon || undefined,
              lucideIcon: group.lucide_icon || undefined,
              kind: 'entry' as const,
              mine: entry.mine,
              keywords,
              preview: previewOf(entry.chip),
            };
          }),
        };
      }
    );

    return {
      sections: [...recentSections, ...nextSections],
      chipsByKey: chips,
    };
  }, [suggestions, templatesOn, recentTemplates]);

  const handlePick = (key: string) => {
    const chip = chipsByKey.get(key);
    if (chip === undefined) return;
    if (needsFill(chip, fillContext)) {
      setFilling({ key, chip });
      return;
    }
    onSelect(chip);
    onClose();
  };

  const fill: PromptFill | null = filling && {
    key: filling.key,
    mode: 'insert',
    chip: filling.chip,
    onSubmit: chip => {
      onSelect(chip);
      onClose();
    },
    // Back to the list with the search as typed.
    onBack: () => setFilling(null),
  };

  return (
    <Popover
      isOpen={isOpen}
      onOpen={onOpen}
      onClose={onClose}
      placement='top-start'
      strategy='fixed'
      isLazy
      closeOnBlur
      closeOnEsc
      // The menu focuses its own search field.
      autoFocus={false}
      gutter={8}
    >
      <PopoverTrigger>
        <IconButton
          ref={triggerRef}
          variant='glassy'
          size='sm'
          colorScheme='gray'
          zIndex={3}
          icon={<Plus size={16} />}
          disabled={disabled}
          aria-label='Insert command'
          aria-haspopup='dialog'
          aria-expanded={isOpen}
          data-cy='composer-command-picker'
        />
      </PopoverTrigger>
      <Portal>
        <PopoverContent
          w='auto'
          maxW='calc(100vw - 16px)'
          // The menu takes its height from the room left and scrolls inside.
          maxH='calc(100dvh - 16px)'
          display='flex'
          flexDirection='column'
          bg='transparent'
          border='none'
          boxShadow='none'
          p={0}
          _focus={{ boxShadow: 'none' }}
          _focusVisible={{ boxShadow: 'none' }}
          aria-label='Commands'
        >
          <PromptCommandMenu
            sections={sections}
            onPick={handlePick}
            enterHint='to insert'
            placeholder='Search prompts…'
            autoFocus
            onClose={onClose}
            fill={fill}
            paneAnchor={triggerRef}
          />
        </PopoverContent>
      </Portal>
    </Popover>
  );
}
